import { extractStory } from './text'

// Calls Vercel serverless functions which proxy to Anthropic and GitHub
// This avoids CORS issues and keeps API keys server-side only

let cachedInstructions = null

async function fetchInstructions() {
  if (cachedInstructions) return cachedInstructions
  try {
    const res = await fetch('/api/instructions')
    if (res.ok) {
      cachedInstructions = await res.text()
      return cachedInstructions
    }
  } catch (e) {
    console.warn('Could not fetch instructions:', e)
  }
  return null
}

async function callClaude(body) {
  const res = await fetch('/api/claude', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })
  const data = await res.json()
  if (data.error) throw new Error(data.error)
  // With web search the reply interleaves text with tool blocks; the answer is
  // the text after the last tool block (earlier text is "let me search…" chatter).
  let lastTool = -1
  data.content.forEach((b, i) => { if (b.type !== 'text') lastTool = i })
  const joinText = blocks => blocks.filter(b => b.type === 'text').map(b => b.text).join('\n').trim()
  const text = joinText(data.content.slice(lastTool + 1)) || joinText(data.content)
  // Strip any MEMORY_UPDATE block that bleeds in from instructions.txt
  const memIdx = text.indexOf('MEMORY_UPDATE')
  return memIdx > -1 ? text.slice(0, memIdx).trim() : text
}

// ── Story proposals ───────────────────────────────────────
export async function proposeStories() {
  const instructions = await fetchInstructions()
  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  })

  const editorialContext = instructions
    ? `You are working from these editorial instructions:\n\n${instructions}\n\n`
    : `You are the editorial AI for Inside Mag, a daily digest covering human-powered movement in the mountains. Culture first, athletic endeavour second. Multi-sport adventures get prime billing.\n\n`

  const prompt = `${editorialContext}Today is ${today}.

Search the web for the most significant news from the past 7 days relevant to Inside Mag's coverage areas. Check the PREFERRED SOURCES listed in the editorial instructions first, then widen to the general web.

OUTPUT INSTRUCTIONS: Respond with ONLY a valid JSON object. No preamble, no explanation, no markdown fences. Start your response with { and end with }. Do not write anything before or after the JSON.

Propose stories in this exact structure:
{
  "special_features": [6 items],
  "notable_ascents": [10 items],
  "gear": [3 items],
  "regular": [6 items]
}

Each special_feature:
{ "sport_slug": "ALL CAPS", "headline": "...", "summary": "max 25 words", "angles": [3 items with "label" and "description" max 20 words each] }

Each notable_ascent:
{ "headline": "Bold title", "description": "Under 35 words, publication-ready.", "angles": [{"label": "Include", "description": "Include"}] }

Each gear item:
{ "sport_slug": "GEAR", "headline": "...", "summary": "max 25 words", "gear_type": "one of: garage-tinkerer|buy-it-for-life|major-announcement|gear-history|patent-leak", "angles": [2 items max 20 words each] }

Each regular item:
{ "sport_slug": "ALL CAPS", "headline": "...", "summary": "max 25 words", "rubric_type": "rubric name", "angles": [2 items max 20 words each] }`

  const raw = await callClaude({
    model: 'claude-sonnet-4-6',
    max_tokens: 16000,
    tools: [{ type: 'web_search_20250305', name: 'web_search' }],
    messages: [{ role: 'user', content: prompt }]
  })

  try {
    // Find the JSON object anywhere in the response
    const jsonStart = raw.indexOf('{')
    const jsonEnd = raw.lastIndexOf('}')
    if (jsonStart === -1 || jsonEnd === -1) throw new Error('No JSON found')
    const jsonStr = raw.slice(jsonStart, jsonEnd + 1)
    return JSON.parse(jsonStr)
  } catch (e) {
    console.error('Failed to parse story proposals. Raw response:', raw?.slice(0, 300))
    return { special_features: [], notable_ascents: [], gear: [], regular: [] }
  }
}

// ── Story draft ───────────────────────────────────────────
export async function draftStory(story, chosenAngle) {
  const instructions = await fetchInstructions()
  const isSpecial = story.story_type === 'special'
  const isNotableAscent = story.story_type === 'notable_ascent'
  const isGear = story.sport_slug === 'GEAR'
  const wordLimit = isSpecial ? '175–200' : isNotableAscent ? '40' : '75–125'

  const editorialVoice = instructions
    ? `Follow these editorial instructions:\n\n${instructions}\n\n`
    : `Voice: direct, first-hand, specific. Active voice. Banned: "hits differently", "game-changer", "pushes the boundaries", "send" (climbing sense).\n\n`

  const prompt = `${editorialVoice}Write a story for Inside Mag.

STORY:
Sport: ${story.sport_slug}
Headline: ${story.headline}
Angle: ${chosenAngle.label} — ${chosenAngle.description}
Type: ${isSpecial ? 'Special Feature' : isGear ? 'Gear Wall' : isNotableAscent ? 'Notable Ascent' : 'Regular Feature'}
Word limit: ${wordLimit} words

FORMAT:
${isSpecial ? `- First line: ${story.sport_slug} (plain text), then a blank line
- Next: ## ${story.headline}, then a blank line
- Continuous prose, no headers
- Include [Read more](url) links` : ''}
${isNotableAscent ? '- Single bullet: - **Bold title:** under 40 words.' : ''}
${isGear ? `- ## ${story.headline}
- Bias against gimmickry. Gear that simplifies, lasts, disappears underneath you.` : ''}
${!isSpecial && !isNotableAscent && !isGear ? `- ## ${story.headline}
- 75–125 words, no sport slug` : ''}

Do all research silently. Your reply must be ONLY the finished story, beginning with the first line of the format above: no notes, no summary of what you found, no "let me" commentary, no MEMORY_UPDATE block, no JSON.`

  const text = await callClaude({
    model: 'claude-sonnet-4-6',
    max_tokens: 2500,
    tools: [{ type: 'web_search_20250305', name: 'web_search' }],
    messages: [{ role: 'user', content: prompt }]
  })
  return extractStory(text, story.headline)
}

// ── Intro generation ──────────────────────────────────────
export async function generateIntro(stories, sendDate) {
  const date = sendDate
    ? new Date(sendDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    : 'today'
  const storyContext = stories.slice(0, 3)
    .map((s, i) => `${i + 1}. ${s.sport_slug}: ${s.headline}`)
    .join('\n')

  const prompt = `Write the opening for an Inside Mag digest dated ${date}.

Stories in this edition:
${storyContext}

Rules:
- 60 words maximum
- Warm, direct, chatty — a note from someone paying close attention
- Tease the stories without listing them robotically
- Do NOT start with "Welcome"
- Write in sentence case — only capitalise the first word and proper nouns. Do not capitalise every word.

Also write a short email subject line: sentence case, capitalise proper nouns/acronyms, no trailing punctuation.

Respond with JSON only:
{
  "intro": "opening paragraph text",
  "subject": "email subject line"
}`

  const raw = await callClaude({
    model: 'claude-sonnet-4-6',
    max_tokens: 500,
    messages: [{ role: 'user', content: prompt }]
  })

  try {
    const clean = raw.replace(/```json|```/g, '').trim()
    return JSON.parse(clean)
  } catch {
    return { intro: raw, subject: stories[0]?.headline || 'This week in the mountains' }
  }
}
